import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GetAllFollowUpTypes,
  fetchProjectData,
  GetLeadLog,
  LeadLgoList,
  fetchLeadData,
  Leads,
  createUserTask,
} from "../Services/APIService";
import Select from "react-select";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import toast, { Toaster } from "react-hot-toast";

export interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Project {
  projectID: number;
  projectName: string;
}

interface SelectOption {
  value: number;
  label: string;
  projectData: Project;
}

interface SelectLeadOption {
  value: number;
  label: string;
  leadData: Leads;
}

interface FormData {
  reportTo: SelectOption | null;
  note: string;
  followupDate: string;
  reminderDate: string;
  leadData?: SelectLeadOption | null;
  isPostponed: boolean;
  postponeReason: string;
  rescheduleDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string;
}

interface CreateUserTaskRequest {
  leadId: number;
  logId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  reminderDate: string;
  tenantId: number;
  createdBy: number;
}

interface UpdateUserTaskRequest {
  id: number;
  tenantId: number;
  leadId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string;
  rescheduleDate: string;
  reminderDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string;
  modifiedBy: number;
}

// Add this function to your APIService.js file
const updateUserTask = async (requestData: UpdateUserTaskRequest) => {
  try {
    const accessToken = localStorage.getItem("authToken");
    const response = await fetch(
      "https://rprocrmsasdevapi.rprocrm.com/api/LeadAction/UpdateUserTask",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          // Add any authentication headers if needed
        },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error updating user task:", error);
    throw error;
  }
};

const EditUserTask: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [projectData, setprojectData] = useState<Project[]>([]);
  const [projectOption, setProjectOption] = useState<SelectOption[]>([]);
  const [allowCreateTaskLeadOption, setallowCreateTaskLeadOption] = useState<
    SelectLeadOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskData, setTaskData] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    reportTo: null,
    note: "",
    followupDate: "",
    reminderDate: "",
    leadData: null,
    isPostponed: false,
    postponeReason: "",
    rescheduleDate: "",
    isCompleted: false,
    isCanceled: false,
    taskCompletionDate: "",
  });
  const [leadLogData, setLeadLogData] = useState<LeadLgoList[]>([]);

  useEffect(() => {
    const getFetchpMasteData = async () => {
      setIsLoading(true);

      try {
        const taskDataFromLocation = location.state?.taskData;
        console.log("Task Data:", taskDataFromLocation);
        setTaskData(taskDataFromLocation);

        //fetch followup types
        const fetchAllFolowUpType = await GetAllFollowUpTypes();
        const activeTypes = fetchAllFolowUpType.filter(
          (type: followupTypeList) => type.isActive
        );
        setFollowupType(activeTypes);

        // Set active tab based on task data or first active type
        if (taskDataFromLocation?.followupTypeId) {
          setActiveTab(taskDataFromLocation.followupTypeId);
        } else if (activeTypes.length > 0) {
          setActiveTab(activeTypes[0].id);
        }

        //fetch project
        const fetchAllProjectData = await fetchProjectData();
        const options = fetchAllProjectData.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOption(options);
        setprojectData(fetchAllProjectData);

        //FetchLeadLogData
        const leadLogs = await GetLeadLog();
        setLeadLogData(leadLogs);

        //get all lead data
        const fetchAllLeadData = await fetchLeadData();

        // Create dropdown options for all leads (for editing, we might need to show all leads)
        const dropdownOptions = fetchAllLeadData.map((lead) => ({
          value: lead.leadId,
          label: `${lead.firstName} ${lead.lastName}`,
          leadData: lead,
        }));
        setallowCreateTaskLeadOption(dropdownOptions);

        // Populate form data if task data exists
        if (taskDataFromLocation) {
          populateFormWithTaskData(
            taskDataFromLocation,
            options,
            dropdownOptions
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getFetchpMasteData();
  }, [location.state]);

  const populateFormWithTaskData = (
    taskData: any,
    projectOptions: SelectOption[],
    leadOptions: SelectLeadOption[]
  ) => {
    // Find matching project option
    const selectedProject = projectOptions.find(
      (option) => option.value === taskData.projectId
    );

    // Find matching lead option
    const selectedLead = leadOptions.find(
      (option) => option.value === taskData.leadId
    );

    // Format dates for datetime-local inputs
    const formatDateForInput = (dateString: string) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return formatDateTimeLocal(date);
    };

    setFormData({
      reportTo: selectedProject || null,
      leadData: selectedLead || null,
      note: taskData.taskNotes || "",
      followupDate: formatDateForInput(taskData.followupDate),
      reminderDate: formatDateForInput(taskData.reminderDate),
      isPostponed: taskData.isPostponed || false,
      postponeReason: taskData.postponeReason || "",
      rescheduleDate: formatDateForInput(taskData.rescheduleDate),
      isCompleted: taskData.isCompleted || false,
      isCanceled: taskData.isCanceled || false,
      taskCompletionDate: formatDateForInput(taskData.taskCompletionDate),
    });
  };

  const handleTabClick = (tabId: number) => {
    setActiveTab(tabId);
  };

  const handleSingleSelectChange = (selectedOption: SelectOption | null) => {
    setFormData((prev) => ({
      ...prev,
      reportTo: selectedOption,
    }));
  };

  const handleSingleSelectChangeOfLead = (
    SelectLeadOption: SelectLeadOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      leadData: SelectLeadOption,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const handleSubmit = async () => {
    if (!activeTab || !formData.reportTo) {
      alert("Please select a project and ensure all fields are filled");
      return;
    }

    if (
      !formData.followupDate ||
      !formData.reminderDate ||
      !formData.note.trim()
    ) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get values from localStorage
      const tenantId = getLocalStorageValue("tenantId");
      const modifiedBy = getLocalStorageValue("UserId");

      // Prepare the request data for update
      const requestData: UpdateUserTaskRequest = {
        id: taskData?.id || 0,
        tenantId: tenantId,
        leadId: formData.leadData?.value || taskData?.leadId || 0,
        projectId: formData.reportTo.value,
        assignedUser: taskData?.assignedUser || 0,
        followupTypeId: activeTab,
        followupDate: formData.followupDate,
        taskNotes: formData.note,
        isPostponed: formData.isPostponed,
        postponeReason: formData.postponeReason,
        rescheduleDate: formData.rescheduleDate || new Date().toISOString(),
        reminderDate: formData.reminderDate,
        isCompleted: formData.isCompleted,
        isCanceled: formData.isCanceled,
        taskCompletionDate:
          formData.taskCompletionDate || new Date().toISOString(),
        modifiedBy: modifiedBy,
      };

      // Call the Update API
      const result = await updateUserTask(requestData);

      toast.success("Task updated successfully!");

      // Optionally navigate back or refresh data
      // navigate(-1); // Go back to previous page
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error updating task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format datetime-local input value
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Set default datetime values only if no task data exists
  useEffect(() => {
    if (!taskData) {
      const now = new Date();
      const reminderTime = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour

      if (!formData.followupDate) {
        setFormData((prev) => ({
          ...prev,
          followupDate: formatDateTimeLocal(now),
        }));
      }
      if (!formData.reminderDate) {
        setFormData((prev) => ({
          ...prev,
          reminderDate: formatDateTimeLocal(reminderTime),
        }));
      }
    }
  }, [taskData]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <ul
                className="nav nav-tabs cardHeaderHegiht"
                id="myTab"
                role="tablist"
                style={{ borderBottom: "1px solid rgb(222 226 230)" }}
              >
                {followupType.map((type) => (
                  <li key={type.id} className="nav-item" role="presentation">
                    <button
                      className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${
                        activeTab === type.id ? "active" : ""
                      }`}
                      id={`tab-${type.id}`}
                      onClick={() => handleTabClick(type.id)}
                      type="button"
                      role="tab"
                      aria-controls={`tab-${type.id}-pane`}
                      aria-selected={activeTab === type.id}
                    >
                      {type.name}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="tab-content card-body" id="myTabContent">
                {followupType.map((type) => (
                  <div
                    key={type.id}
                    className={`tab-pane fade ${
                      activeTab === type.id ? "show active" : ""
                    }`}
                    id={`tab-${type.id}-pane`}
                    role="tabpanel"
                    aria-labelledby={`tab-${type.id}`}
                    tabIndex={0}
                  >
                    <div>
                      <div className="row">
                        <div className="col-12 col-lg-6 mb-3">
                          <label className="form-label">
                            Select Project{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={projectOption}
                            value={formData.reportTo}
                            onChange={handleSingleSelectChange}
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Select project"
                            isLoading={isLoading}
                            isClearable
                            isSearchable
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label className="form-label">
                            Select Lead <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={allowCreateTaskLeadOption}
                            value={formData.leadData}
                            onChange={handleSingleSelectChangeOfLead}
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Select Lead"
                            isLoading={isLoading}
                            isClearable
                            isSearchable
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label htmlFor="followupDate" className="form-label">
                            Followup Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            id="followupDate"
                            name="followupDate"
                            className="form-control form-control-lg"
                            value={formData.followupDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label htmlFor="reminderDate" className="form-label">
                            Reminder Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            id="reminderDate"
                            name="reminderDate"
                            className="form-control form-control-lg"
                            value={formData.reminderDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-12 mb-3">
                          <label htmlFor="note" className="form-label">
                            Task Note <span className="text-danger">*</span>
                          </label>
                          <textarea
                            onChange={handleInputChange}
                            id="note"
                            name="note"
                            className="form-control form-control-lg"
                            placeholder="Enter Task Note"
                            value={formData.note}
                            rows={4}
                            required
                          />
                        </div>

                        {/* Additional fields for task status */}
                        <div className="col-12 col-lg-4 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="isPostponed"
                              name="isPostponed"
                              checked={formData.isPostponed}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="isPostponed"
                            >
                              Is Postponed
                            </label>
                          </div>
                        </div>

                        <div className="col-12 col-lg-4 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="isCompleted"
                              name="isCompleted"
                              checked={formData.isCompleted}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="isCompleted"
                            >
                              Is Completed
                            </label>
                          </div>
                        </div>

                        <div className="col-12 col-lg-4 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="isCanceled"
                              name="isCanceled"
                              checked={formData.isCanceled}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="isCanceled"
                            >
                              Is Canceled
                            </label>
                          </div>
                        </div>

                        {formData.isPostponed && (
                          <>
                            <div className="col-12 col-lg-6 mb-3">
                              <label
                                htmlFor="postponeReason"
                                className="form-label"
                              >
                                Postpone Reason
                              </label>
                              <input
                                type="text"
                                id="postponeReason"
                                name="postponeReason"
                                className="form-control form-control-lg"
                                placeholder="Enter postpone reason"
                                value={formData.postponeReason}
                                onChange={handleInputChange}
                              />
                            </div>

                            <div className="col-12 col-lg-6 mb-3">
                              <label
                                htmlFor="rescheduleDate"
                                className="form-label"
                              >
                                Reschedule Date
                              </label>
                              <input
                                type="datetime-local"
                                id="rescheduleDate"
                                name="rescheduleDate"
                                className="form-control form-control-lg"
                                value={formData.rescheduleDate}
                                onChange={handleInputChange}
                              />
                            </div>
                          </>
                        )}

                        {formData.isCompleted && (
                          <div className="col-12 col-lg-6 mb-3">
                            <label
                              htmlFor="taskCompletionDate"
                              className="form-label"
                            >
                              Task Completion Date
                            </label>
                            <input
                              type="datetime-local"
                              id="taskCompletionDate"
                              name="taskCompletionDate"
                              className="form-control form-control-lg"
                              value={formData.taskCompletionDate}
                              onChange={handleInputChange}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="card-footer">
                <button
                  className="btn btn-primary me-2"
                  onClick={handleSubmit}
                  disabled={!activeTab || !formData.reportTo || isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Task"}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default EditUserTask;
